import React, { useState, useRef, useEffect, useCallback } from "react";
import api from "../utils/api";
import { Upload, FileText, X, Check, Save, Bookmark, AlertCircle, Loader2, Plus, Layers } from "lucide-react";

const MAX_FILES_DEFAULT = 12;

/**
 * Unified file-slot component for upload + sheet + skip_rows + template + multi-file merge.
 *
 * Props:
 *   file             : { file_id, filename, sheets, columns, row_count, sheet, skip_rows,
 *                        extra_files: [{file_id, filename, sheets, sheet}],
 *                        merge_mode: "superset"|"strict" } | null
 *   onChange(patch)  : merge patch into parent state (parent owns `file`)
 *   onClear()
 *   label, accent    : styling
 *   compact          : render a compact card
 *   maxFiles         : maximum total files in this slot (default 12)
 */
export default function FileSlot({
  file, onChange, onClear, label = "Upload file", accent = "var(--sage)",
  compact = false, testIdPrefix = "fs", maxFiles = MAX_FILES_DEFAULT,
}) {
  const ref = useRef();
  const extraRefs = useRef({});
  const [uploading, setUploading] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [saving, setSaving] = useState(false);
  const [mappingOpenIdx, setMappingOpenIdx] = useState(null);

  // Load templates once when file present
  useEffect(() => {
    if (!file) return;
    api.get("/file-templates").then(r => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, [file?.file_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setError("Only .xlsx, .xls, or .csv"); return;
    }
    setError(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/reconciliation/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...res.data, sheet: res.data.sheets?.[0] || null, skip_rows: 0, extra_files: [], merge_mode: "superset" });
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleExtraFile = async (f) => {
    if (!f || !file) return;
    const totalFiles = 1 + (file.extra_files || []).length;
    if (totalFiles >= maxFiles) { setError(`Max ${maxFiles} files per slot`); return; }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) { setError("Only .xlsx, .xls, or .csv"); return; }
    setError(""); setUploadingExtra(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/reconciliation/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const primaryCols = new Set(file.columns || []);
      const extraCols = res.data.columns || [];
      const unmappedCols = extraCols.filter(c => !primaryCols.has(c));
      const newExtra = {
        file_id: res.data.file_id,
        filename: res.data.filename,
        sheets: res.data.sheets || [],
        sheet: res.data.sheets?.[0] || null,
        columns: extraCols,
        col_mapping: {},
        unmapped_cols: unmappedCols,
      };
      onChange({ ...file, extra_files: [...(file.extra_files || []), newExtra] });
      if (unmappedCols.length > 0) setMappingOpenIdx((file.extra_files || []).length);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingExtra(false); }
  };

  const removeExtra = (idx) => {
    const updated = (file.extra_files || []).filter((_, i) => i !== idx);
    onChange({ ...file, extra_files: updated });
    if (mappingOpenIdx === idx) setMappingOpenIdx(null);
    setError("");
  };

  const updateExtraSheet = (idx, sheet) => {
    const updated = (file.extra_files || []).map((ef, i) => i === idx ? { ...ef, sheet } : ef);
    onChange({ ...file, extra_files: updated });
  };

  const updateColMapping = (idx, extraCol, primaryCol) => {
    const updated = (file.extra_files || []).map((ef, i) => {
      if (i !== idx) return ef;
      const newMapping = { ...ef.col_mapping };
      if (primaryCol === "") {
        delete newMapping[extraCol];
      } else {
        newMapping[extraCol] = primaryCol;
      }
      return { ...ef, col_mapping: newMapping };
    });
    onChange({ ...file, extra_files: updated });
  };

  const runPreview = useCallback(async (sheet, skip_rows) => {
    if (!file) return;
    setPreviewing(true); setError("");
    try {
      const res = await api.post("/reconciliation/file-preview", {
        file_id: file.file_id,
        sheet_name: sheet || null,
        skip_rows: Number(skip_rows || 0),
      });
      onChange({
        ...file,
        sheet,
        skip_rows: Number(skip_rows || 0),
        columns: res.data.columns,
        row_count: res.data.row_count,
        preview: res.data.preview,
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  }, [file, onChange]);

  const changeSheet = (s) => runPreview(s, file.skip_rows || 0);
  const changeSkip = (n) => {
    const v = Math.max(0, parseInt(n || "0", 10) || 0);
    runPreview(file.sheet, v);
  };

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const t = templates.find(x => x.id === tplId);
    if (!t) return;
    const sheet = (t.sheet_name && file.sheets?.includes(t.sheet_name)) ? t.sheet_name : (file.sheet || null);
    runPreview(sheet, t.skip_rows || 0);
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/file-templates", {
        name: tplName.trim(),
        sheet_name: file.sheet || null,
        skip_rows: Number(file.skip_rows || 0),
      });
      setTemplates(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setSaveOpen(false); setTplName("");
    } catch (e) {
      alert(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  if (!file) {
    return (
      <div data-testid={`${testIdPrefix}-empty`}>
        {label && <label style={{ fontSize: "0.78rem", fontWeight: 600, color: accent, display: "block", marginBottom: "0.3rem" }}>{label}</label>}
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv" onChange={e => handleFile(e.target.files?.[0])} style={{ display: "none" }} />
        <button
          data-testid={`${testIdPrefix}-upload-btn`}
          onClick={() => ref.current?.click()}
          disabled={uploading}
          className="btn-secondary"
          style={{ width: "100%", padding: compact ? "0.75rem" : "2rem 1rem", fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", borderStyle: "dashed", borderColor: accent, color: accent }}>
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Choose Excel / CSV</>}
        </button>
        {error && <p data-testid={`${testIdPrefix}-error`} style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: "0.3rem" }}>{error}</p>}
      </div>
    );
  }

  const hasSheets = (file.sheets || []).length > 0;
  const extraFiles = file.extra_files || [];
  const totalFiles = 1 + extraFiles.length;
  const canAddMore = totalFiles < maxFiles;

  return (
    <div data-testid={`${testIdPrefix}-card`}>
      {/* Primary file card */}
      <div className="recon-card" style={{ padding: "0.9rem 1rem", borderLeft: `3px solid ${accent}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
            <FileText size={16} style={{ color: accent, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 600, fontSize: "0.85rem", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.filename}</p>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", margin: 0 }}>
                {file.row_count ?? 0} rows · {(file.columns || []).length} columns{previewing && " · refreshing…"}
                {totalFiles > 1 && <span style={{ color: accent, marginLeft: 6, fontWeight: 600 }}>+{extraFiles.length} merged</span>}
              </p>
            </div>
          </div>
          <button onClick={() => { setError(""); onClear(); }} data-testid={`${testIdPrefix}-clear`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={14} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: hasSheets ? "1fr 120px" : "1fr 120px", gap: "0.5rem", marginTop: "0.6rem", alignItems: "end" }}>
          {hasSheets ? (
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Sheet</label>
              <select
                data-testid={`${testIdPrefix}-sheet`}
                value={file.sheet || file.sheets[0]}
                onChange={e => changeSheet(e.target.value)}
                className="recon-input" style={{ width: "100%", fontSize: "0.78rem" }}>
                {file.sheets.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Sheet</label>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", padding: "0.38rem 0.5rem" }}>— (single sheet)</div>
            </div>
          )}
          <div>
            <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Skip top rows</label>
            <input
              data-testid={`${testIdPrefix}-skip`}
              type="number" min={0} max={200}
              value={file.skip_rows ?? 0}
              onChange={e => changeSkip(e.target.value)}
              className="recon-input" style={{ width: "100%", fontSize: "0.78rem" }} />
          </div>
        </div>

        <div style={{ marginTop: "0.6rem", display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", flex: "1 1 auto", minWidth: "160px" }}>
            <Bookmark size={12} style={{ color: "var(--text-muted)" }} />
            <select
              data-testid={`${testIdPrefix}-tpl-picker`}
              value=""
              onChange={e => { applyTemplate(e.target.value); e.target.value = ""; }}
              className="recon-input"
              style={{ fontSize: "0.75rem", flex: 1, minWidth: "140px" }}
            >
              <option value="">Load template…</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.sheet_name || "—"} / skip {t.skip_rows})
                </option>
              ))}
            </select>
          </div>
          <button
            data-testid={`${testIdPrefix}-save-tpl`}
            onClick={() => { setSaveOpen(true); setTplName(""); }}
            className="btn-secondary"
            style={{ fontSize: "0.72rem", padding: "0.3rem 0.55rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
            <Save size={11} /> Save as template
          </button>
        </div>

        {(file.columns || []).length > 0 && (
          <div style={{ marginTop: "0.55rem" }}>
            <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: "0.2rem" }}>Detected columns:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
              {file.columns.slice(0, 8).map((c, i) => (
                <span key={`${c}-${i}`} style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.68rem", background: "var(--bg-subtle)", padding: "0.1rem 0.45rem", borderRadius: "0.25rem", color: "var(--text-secondary)" }}>{c}</span>
              ))}
              {file.columns.length > 8 && (
                <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>+{file.columns.length - 8} more</span>
              )}
            </div>
          </div>
        )}

        {(file.columns || []).length === 0 && !previewing && (
          <div style={{ marginTop: "0.5rem", padding: "0.45rem 0.6rem", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "0.35rem", fontSize: "0.72rem", color: "#92400E", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <AlertCircle size={11} /> Couldn't detect headers. Try increasing "Skip top rows".
          </div>
        )}

        {error && <p data-testid={`${testIdPrefix}-error`} style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: "0.35rem" }}>{error}</p>}
      </div>

      {/* Extra files list */}
      {extraFiles.map((ef, idx) => {
        const hasUnmapped = (ef.unmapped_cols || []).length > 0;
        const isMappingOpen = mappingOpenIdx === idx;
        return (
          <div key={ef.file_id} data-testid={`${testIdPrefix}-extra-${idx}`} style={{ marginTop: "0.4rem" }}>
            <div style={{ padding: "0.55rem 0.75rem", borderRadius: isMappingOpen ? "0.5rem 0.5rem 0 0" : "0.5rem", border: "1px solid var(--border-default)", background: "var(--bg-subtle)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Layers size={12} style={{ color: accent, flexShrink: 0 }} />
              <span style={{ fontSize: "0.78rem", fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ef.filename}</span>
              {(ef.sheets || []).length > 1 && (
                <select value={ef.sheet || ""} onChange={e => updateExtraSheet(idx, e.target.value)}
                  className="recon-input" style={{ fontSize: "0.72rem", width: "110px" }}>
                  {ef.sheets.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>skip {file.skip_rows || 0}</span>
              {hasUnmapped && (
                <button onClick={() => setMappingOpenIdx(isMappingOpen ? null : idx)} data-testid={`${testIdPrefix}-map-cols-${idx}`}
                  style={{ fontSize: "0.65rem", padding: "0.1rem 0.35rem", borderRadius: "9999px", border: "1px solid #FCD34D", background: "#FFFBEB", color: "#B45309", cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "0.2rem" }}>
                  <AlertCircle size={10} /> {ef.unmapped_cols.length} col{ef.unmapped_cols.length !== 1 ? "s" : ""} unmatched
                </button>
              )}
              <button onClick={() => removeExtra(idx)} data-testid={`${testIdPrefix}-remove-extra-${idx}`}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: "0.1rem" }}>
                <X size={12} />
              </button>
            </div>
            {/* Column mapping panel */}
            {isMappingOpen && hasUnmapped && (
              <div style={{ padding: "0.6rem 0.75rem", border: "1px solid #FCD34D", borderTop: "none", borderRadius: "0 0 0.5rem 0.5rem", background: "#FFFBEB" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#92400E", marginBottom: "0.4rem" }}>
                  Map columns — "{ef.filename}" has columns not in the primary file:
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                  {(ef.unmapped_cols || []).map(col => (
                    <div key={col} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.75rem" }}>
                      <span style={{ fontFamily: "JetBrains Mono, monospace", background: "#FEF3C7", padding: "0.15rem 0.4rem", borderRadius: "0.25rem", minWidth: "120px", color: "#B45309", fontWeight: 600 }}>{col}</span>
                      <span style={{ color: "var(--text-muted)", fontSize: "0.72rem" }}>→</span>
                      <select data-testid={`${testIdPrefix}-col-map-${idx}-${col}`}
                        value={(ef.col_mapping || {})[col] || ""}
                        onChange={e => updateColMapping(idx, col, e.target.value)}
                        style={{ fontSize: "0.75rem", padding: "0.2rem 0.4rem", border: "1px solid var(--border-default)", borderRadius: "0.25rem", background: "white", flex: 1 }}>
                        <option value="">Keep as new column</option>
                        {(file.columns || []).map(pc => <option key={pc} value={pc}>{pc}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <button onClick={() => setMappingOpenIdx(null)} style={{ marginTop: "0.4rem", fontSize: "0.7rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Done</button>
              </div>
            )}
          </div>
        );
      })}

      {/* Merge mode selector — shown when > 1 file */}
      {totalFiles > 1 && (
        <div style={{ marginTop: "0.4rem", display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.4rem 0.5rem", background: "var(--bg-subtle)", borderRadius: "0.375rem", border: "1px solid var(--border-default)" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)" }}>Merge mode:</span>
          {["superset", "strict"].map(m => (
            <label key={m} style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer", fontSize: "0.75rem", color: file.merge_mode === m ? accent : "var(--text-muted)" }}>
              <input type="radio" name={`${testIdPrefix}-merge-mode`} value={m} checked={(file.merge_mode || "superset") === m}
                onChange={() => onChange({ ...file, merge_mode: m })} />
              {m === "superset" ? "Superset (fill gaps)" : "Strict (identical cols)"}
            </label>
          ))}
          <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginLeft: "auto" }}>
            {totalFiles} files · skip {file.skip_rows || 0} rows applied to all
          </span>
        </div>
      )}

      {/* Add another file */}
      {canAddMore && (
        <>
          <input
            ref={el => { if (el) extraRefs.current["add"] = el; }}
            type="file" accept=".xlsx,.xls,.csv"
            onChange={e => handleExtraFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
          <button
            data-testid={`${testIdPrefix}-add-extra`}
            onClick={() => extraRefs.current["add"]?.click()}
            disabled={uploadingExtra}
            style={{ marginTop: "0.4rem", background: "none", border: `1px dashed ${accent}40`, borderRadius: "0.375rem", padding: "0.4rem 0.75rem", cursor: "pointer", color: accent, fontSize: "0.75rem", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem" }}>
            {uploadingExtra ? <><Loader2 size={11} className="animate-spin" /> Uploading…</> : <><Plus size={11} /> Add another file to merge</>}
          </button>
        </>
      )}

      {/* Save template modal */}
      {saveOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setSaveOpen(false)} />
          <div data-testid={`${testIdPrefix}-save-modal`} style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.25rem 1.5rem", width: "100%", maxWidth: "420px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", margin: "0 0 0.3rem" }}>Save file template</h3>
            <p style={{ fontSize: "0.78rem", color: "var(--text-secondary)", margin: "0 0 0.75rem" }}>
              Current settings — Sheet: <strong>{file.sheet || "—"}</strong>, Skip rows: <strong>{file.skip_rows || 0}</strong>. Shared within your department.
            </p>
            <label style={{ fontSize: "0.74rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Template name</label>
            <input
              data-testid={`${testIdPrefix}-tpl-name-input`}
              autoFocus
              className="recon-input"
              value={tplName}
              onChange={e => setTplName(e.target.value)}
              placeholder="e.g. Bank statement – 5-row banner"
              style={{ width: "100%", fontSize: "0.85rem", marginBottom: "0.9rem" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem" }}>
              <button onClick={() => setSaveOpen(false)} className="btn-secondary" style={{ fontSize: "0.82rem" }}>Cancel</button>
              <button
                data-testid={`${testIdPrefix}-tpl-save-confirm`}
                onClick={saveAsTemplate}
                disabled={!tplName.trim() || saving}
                className="btn-primary"
                style={{ fontSize: "0.82rem", opacity: (!tplName.trim() || saving) ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                <Check size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
